/**
 * Search Template Service
 * API service for managing AI search templates
 */

import axios from '../config/axios';

export interface SearchTemplate {
  id: number;
  template_name: string;
  description?: string;
  category?: string;
  system_prompt_template: string;
  response_schema?: Record<string, any>;
  expected_response?: string;
  model_name: string;
  max_tokens: number;
  temperature: number;
  timeout_seconds: number;
  cache_enabled: boolean;
  cache_ttl_seconds: number;
  is_active: boolean;
  requires_auth: boolean;
  allowed_roles?: string[];
  created_by?: number;
  created_at: string;
  updated_at?: string;
  total_requests: number;
  last_used_at?: string;
}

export interface CreateSearchTemplateRequest {
  template_name: string;
  description?: string;
  category?: string;
  system_prompt_template: string;
  response_schema?: Record<string, any>;
  expected_response?: string;
  model_name?: string;
  max_tokens?: number;
  temperature?: number;
  timeout_seconds?: number;
  cache_enabled?: boolean;
  cache_ttl_seconds?: number;
  requires_auth?: boolean;
  allowed_roles?: string[];
}

export interface UpdateSearchTemplateRequest {
  description?: string;
  system_prompt_template?: string;
  response_schema?: Record<string, any>;
  expected_response?: string;
  model_name?: string;
  max_tokens?: number;
  temperature?: number;
  timeout_seconds?: number;
  cache_enabled?: boolean;
  cache_ttl_seconds?: number;
  is_active?: boolean;
  requires_auth?: boolean;
  allowed_roles?: string[];
}

export interface TemplateStats {
  template_id: number;
  template_name: string;
  total_requests: number;
  last_used_at?: string;
  is_active: boolean;
  created_at: string;
  cache_enabled: boolean;
  model_name: string;
}

export interface GenericSearchRequest {
  template_id: number;
  query: string;
  limit?: number;
  additional_params?: Record<string, any>;
}

export interface GenericSearchResponse {
  success: boolean;
  template_id: number;
  template_name: string;
  category?: string;
  query: string;
  total_results: number;
  results: any;
  cached: boolean;
  processing_time_seconds?: number;
  request_id: string;
}

class SearchTemplateService {
  private getAuthToken(): string | null {
    return localStorage.getItem('authToken');
  }

  private getHeaders() {
    const token = this.getAuthToken();
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }

  /**
   * List all search templates
   */
  async listTemplates(
    skip = 0,
    limit = 100,
    activeOnly = false,
    category?: string
  ): Promise<SearchTemplate[]> {
    const params: any = { skip, limit };
    if (activeOnly) params.active_only = true;
    if (category) params.category = category;

    const response = await axios.get("/api/scribing/admin/templates", {
      headers: this.getHeaders(),
      params,
    });
    return response.data;
  }

  /**
   * Get a specific template by ID
   */
  async getTemplate(templateId: number): Promise<SearchTemplate> {
    const response = await axios.get(
      `/api/scribing/admin/templates/${templateId}`,
      {
        headers: this.getHeaders(),
      }
    );
    return response.data;
  }

  /**
   * Create a new search template
   */
  async createTemplate(template: CreateSearchTemplateRequest): Promise<SearchTemplate> {
    const response = await axios.post(
      '/api/scribing/admin/templates',
      template,
      { headers: this.getHeaders() }
    );
    return response.data;
  }

  /**
   * Update an existing template
   */
  async updateTemplate(
    templateId: number,
    updates: UpdateSearchTemplateRequest
  ): Promise<SearchTemplate> {
    const response = await axios.put(
      `/api/scribing/admin/templates/${templateId}`,
      updates,
      { headers: this.getHeaders() }
    );
    return response.data;
  }

  /**
   * Delete a template
   */
  async deleteTemplate(templateId: number): Promise<{ success: boolean; message: string }> {
    const response = await axios.delete(
      `/api/scribing/admin/templates/${templateId}`,
      { headers: this.getHeaders() }
    );
    return response.data;
  }

  /**
   * Toggle template active status
   */
  async toggleTemplate(templateId: number): Promise<{
    success: boolean;
    template_id: number;
    template_name: string;
    is_active: boolean;
  }> {
    const response = await axios.post(
      `/api/scribing/admin/templates/${templateId}/toggle`,
      {},
      { headers: this.getHeaders() }
    );
    return response.data;
  }

  /**
   * Get template usage statistics
   */
  async getTemplateStats(templateId: number): Promise<TemplateStats> {
    const response = await axios.get(
      `/api/scribing/admin/templates/${templateId}/stats`,
      { headers: this.getHeaders() }
    );
    return response.data;
  }

  /**
   * Test search using a template
   */
  async testSearch(request: GenericSearchRequest): Promise<GenericSearchResponse> {
    const response = await axios.post(
      '/api/textprocessing/v1/search',
      request,
      { headers: this.getHeaders() }
    );
    return response.data;
  }

  /**
   * Get categories for filtering
   */
  async getCategories(): Promise<string[]> {
    const templates = await this.listTemplates();
    const categories = new Set<string>();
    templates.forEach(t => {
      if (t.category) categories.add(t.category);
    });
    return Array.from(categories);
  }
}

export const searchTemplateService = new SearchTemplateService();
